import './App.css';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Suspense, useCallback, useEffect, useMemo } from 'react';
import GoogleMapsProvider from './components/GoogleMapsProvider';
import SummaryApi from './common';
import Context from './context';
import { useDispatch, useSelector } from 'react-redux';
import { setUserDetails } from './store/userSlice';
import { fetchWishlist, putWishlist } from './store/wishlistSlice';
import { loadWishlist } from './utils/wishlistStorage';
import { fetchServerCartCount, clearServerCart, resetServerCount } from './store/cartSlice';
import useAccessTokenAutoRefresh from './hooks/useAccessTokenAutoRefresh';

// Single fallback used by the route-level <Suspense> below. Each lazy page chunk loads
// quickly enough that a full skeleton is overkill — a short, low-contrast loader keeps the
// header/footer visible and avoids layout shift.
const RouteLoading = () => (
  <div className='min-h-[40vh] flex items-center justify-center text-slate-400 text-sm'>
    Loading…
  </div>
)

function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  // Silent refresh-token rotation. Reads the access_token_expires_at companion cookie
  // on every auth change and schedules a /auth/refresh call ~60s before expiry.
  // No-op when the user is anonymous.
  useAccessTokenAutoRefresh()

  // Handle direct order links from SMS. The backend's `/orders/public/:orderNumber`
  // endpoint 302-redirects to "/" with `?order=<ref>`, so we intercept only on the home
  // page. Previously this fired on ANY URL containing `?order=`, which could hijack any
  // unrelated query param of the same name on any page.
  useEffect(() => {
    if (location.pathname !== '/') return
    const params = new URLSearchParams(location.search)
    const orderRef = params.get('order')
    if (orderRef) {
      navigate(`/orders/public/${orderRef}`, { replace: true })
    }
  }, [location.pathname, location.search, navigate])

  const fetchUserDetails = useCallback(async()=>{
    try {
      const res = await fetch(SummaryApi.current_user.url,{
        method : SummaryApi.current_user.method,
        credentials : 'include'
      })
      if (res.status === 401) return // not logged in yet — expected on first load
      const dataApi = await res.json()
      if(dataApi?.success && dataApi?.data){
        dispatch(setUserDetails(dataApi.data))
      }
    } catch (_) { /* noop */ }
  }, [dispatch])

  // Back-compat Context wrappers. Cart state itself now lives in `cartSlice`; these
  // delegate to thunks so legacy callers like ProductDetails, VerticalCard, COD success
  // page etc. don't have to migrate to `useDispatch` all at once.
  const fetchUserAddToCart = useCallback(() => {
    return dispatch(fetchServerCartCount())
  }, [dispatch])

  const clearCart = useCallback(async () => {
    try {
      const result = await dispatch(clearServerCart()).unwrap()
      return result
    } catch (err) {
      return { success: false, message: err?.message || 'Failed to clear cart' }
    }
  }, [dispatch])

  useEffect(()=>{
    // Try to hydrate user from cookie on first mount.
    fetchUserDetails()
  },[fetchUserDetails])

  const authedUserId = useSelector(s => s?.user?.user?._id)

  // Keep the server cart count in sync with auth status.
  useEffect(() => {
    if (authedUserId) {
      dispatch(fetchServerCartCount())
    } else {
      dispatch(resetServerCount())
    }
  }, [authedUserId, dispatch])

  // On login, merge local wishlist with the user's saved wishlist so neither side loses
  // entries. Skipped when the user is anonymous.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!authedUserId) return
      try {
        const remote = await dispatch(fetchWishlist()).unwrap()
        let local = []
        try { local = loadWishlist() } catch (_) { local = [] }
        const union = Array.from(new Set([...(Array.isArray(remote)?remote:[]), ...(Array.isArray(local)?local:[])]))
        if (!cancelled && union.length !== (remote?.length || 0)) {
          await dispatch(putWishlist(union)).unwrap()
        }
      } catch (_) {
        // ignore
      }
    })()
    return () => { cancelled = true }
  }, [authedUserId, dispatch])

  // Stable Context value so consumers don't re-render on every App re-render. The
  // `cartProductCount` field is intentionally absent now — components that need the count
  // read it from Redux via `selectCartCount` instead.
  const contextValue = useMemo(() => ({
    fetchUserDetails,
    fetchUserAddToCart,
    clearCart,
  }), [fetchUserDetails, fetchUserAddToCart, clearCart])

  return (
    <>
      <Context.Provider value={contextValue}>
        <GoogleMapsProvider>
          <ToastContainer
            position='top-center'
          />

          <Header/>
          <main className='min-h-[calc(100vh-120px)] pt-16'>
            {/* Single Suspense boundary covers every lazy-loaded route page. */}
            <Suspense fallback={<RouteLoading/>}>
              <Outlet/>
            </Suspense>
          </main>
          <Footer/>
        </GoogleMapsProvider>
      </Context.Provider>
    </>
  );
}

export default App;
